import { CanvassingAccess } from "./access";
import { dateLabel, isQrSecret, type ProjectSummary, type Session } from "./domain";
import { QrScanner, qrImage } from "./qr";
import {
  actions,
  button,
  confirmAction,
  countdown,
  element,
  errorMessage,
  field,
  heading,
  Lifetime,
  message,
  notice,
  panel,
  runButton,
  selectField,
  type View,
} from "./ui";

async function projectName(title: string, current: string): Promise<string | null> {
  const name = field("Project name", "name");
  name.input.value = current;
  name.input.maxLength = 200;
  const dialog = element("dialog", "modal");
  const form = element("form", "stack", heading(2, title), name.label);
  const submit = element("button", "button", "Save");
  submit.type = "submit";
  form.append(
    actions(
      button("Cancel", "button button--secondary", () => dialog.close()),
      submit,
    ),
  );
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    if (form.reportValidity() && name.input.value.trim()) dialog.close("saved");
  });
  dialog.append(form);
  document.body.append(dialog);
  return new Promise((resolve) => {
    dialog.addEventListener(
      "close",
      () => {
        const value = dialog.returnValue === "saved" ? name.input.value.trim() : null;
        dialog.remove();
        resolve(value);
      },
      { once: true },
    );
    dialog.showModal();
    name.input.focus();
    name.input.select();
  });
}

export class ProjectsView implements View {
  public readonly element = element("div", "page");
  private readonly lifetime = new Lifetime();
  private readonly content = element("div", "project-grid");
  private readonly createArea = element("div");
  private readonly error = errorMessage();
  private creating = false;
  public constructor(
    private readonly access: CanvassingAccess,
    private readonly openProject: () => void,
  ) {
    this.element.append(
      element("p", "eyebrow", access.account.organization.name),
      heading(1, "Canvassing projects"),
      actions(
        button("Create project", "button", () => this.create()),
        runButton("Refresh", () => access.refresh(), this.error),
      ),
      this.error,
      this.createArea,
      this.content,
    );
    this.lifetime.add(access.subscribe(() => this.render()));
    this.render();
  }
  private create(): void {
    if (this.creating) return;
    this.creating = true;
    const name = field("Project name", "name");
    name.input.maxLength = 200;
    const branch = selectField(
      "Branch",
      this.access.branches.map(({ id, name }) => [id, name]),
    );
    branch.select.required = true;
    const register = selectField("Project type", [
      ["", "Streets"],
      ...this.access.registers.map(({ id, label }): [string, string] => [id, label]),
    ]);
    const error = errorMessage();
    const submit = element("button", "button", "Create project");
    submit.type = "submit";
    const form = element(
      "form",
      "stack",
      heading(2, "New project"),
      name.label,
      branch.label,
      register.label,
      notice(
        "The roll or Streets choice is fixed after creation. Street projects need no electoral-roll assignment.",
      ),
      error,
      actions(
        submit,
        button("Cancel", "button button--secondary", () => {
          this.creating = false;
          this.createArea.replaceChildren();
        }),
      ),
    );
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      if (submit.disabled || !form.reportValidity()) return;
      if (!name.input.value.trim()) {
        error.textContent = "Enter a project name.";
        return;
      }
      submit.disabled = true;
      void this.access.api
        .createProject(
          name.input.value.trim(),
          branch.select.value,
          register.select.value || null,
        )
        .then(async () => {
          if (!this.lifetime.active) return;
          this.creating = false;
          this.createArea.replaceChildren();
          await this.access.refresh();
        })
        .catch((failure: unknown) => {
          if (this.lifetime.active) error.textContent = message(failure);
        })
        .finally(() => {
          submit.disabled = false;
        });
    });
    this.createArea.replaceChildren(panel(form));
    name.input.focus();
  }
  private render(): void {
    this.content.replaceChildren(
      ...this.access.projects.map((project) => this.project(project)),
    );
    if (this.access.projects.length === 0)
      this.content.append(
        notice(
          this.access.initialized
            ? "No projects are available. Create a street project or use an assigned electoral roll."
            : "Loading projects…",
        ),
      );
    if (this.access.error) this.error.textContent = message(this.access.error);
  }
  private project(project: ProjectSummary): HTMLElement {
    const roll =
      project.electoral_register_id === null
        ? "Streets"
        : (this.access.registers.find(({ id }) => id === project.electoral_register_id)
            ?.label ?? project.electoral_register_id);
    const rename = runButton(
      "Rename",
      async () => {
        const name = await projectName("Rename project", project.name);
        if (name === null || !this.lifetime.active) return;
        await this.access.api.renameProject(project.id, name);
        await this.access.refresh();
      },
      this.error,
      "button button--secondary",
    );
    const remove = runButton(
      "Delete",
      async () => {
        if (
          !(await confirmAction(
            `Delete ${project.name}?`,
            "This permanently deletes this project's results, notes and sessions, and revokes participants’ access.",
            "Delete project",
          )) ||
          !this.lifetime.active
        )
          return;
        await this.access.api.deleteProject(project.id);
        await this.access.refresh();
      },
      this.error,
      "button button--danger",
    );
    return panel(
      element("p", "eyebrow", `${project.branch.name} · ${roll}`),
      heading(2, project.name),
      element(
        "p",
        "project-count",
        `${project.canvassed_count.toLocaleString("en-GB")} ${project.electoral_register_id === null ? "streets completed" : "voters canvassed"}`,
      ),
      element(
        "p",
        "muted",
        `Created ${dateLabel(project.created_at)}\nUpdated ${dateLabel(project.updated_at)}`,
      ),
      actions(
        button("View project", "button", () => {
          this.access.select({ kind: "project", id: project.id });
          this.openProject();
        }),
        rename,
        remove,
      ),
    );
  }
  public dispose(): void {
    this.lifetime.dispose();
  }
}

export class SessionsView implements View {
  public readonly element = element("div", "page");
  private readonly lifetime = new Lifetime();
  private readonly content = element("div", "project-grid");
  private readonly tool = element("div");
  private readonly error = errorMessage();
  private scanner: QrScanner | null = null;
  private busy = false;
  public constructor(
    private readonly access: CanvassingAccess,
    private readonly canvass: () => void,
  ) {
    this.element.append(
      heading(1, "Canvassing sessions"),
      actions(
        access.manager
          ? button("Start Canvassing Session", "button", () => this.start())
          : button("Scan Session QR Code", "button", () => this.scan()),
        runButton("Refresh", () => access.refresh(), this.error),
      ),
      this.error,
      this.tool,
      this.content,
    );
    this.lifetime.add(access.subscribe(() => this.render()));
    this.render();
  }
  private start(): void {
    const choice = selectField(
      "Project",
      this.access.projects.map(({ id, name, branch }) => [id, `${name} · ${branch.name}`]),
    );
    const error = errorMessage();
    const submit = runButton(
      "Start session",
      async () => {
        if (!choice.select.value)
          throw new Error("Create a project before starting a session.");
        await this.access.startSession(choice.select.value);
        if (this.lifetime.active) this.tool.replaceChildren();
      },
      error,
    );
    this.tool.replaceChildren(
      panel(
        heading(2, "Start a six-hour session"),
        choice.label,
        notice(
          "Sessions can be extended by one hour at a time, up to twelve hours after starting.",
        ),
        error,
        actions(
          submit,
          button("Cancel", "button button--secondary", () => this.tool.replaceChildren()),
        ),
      ),
    );
  }
  private scan(): void {
    this.scanner?.dispose();
    const error = errorMessage();
    const scanner = new QrScanner(isQrSecret, (code) => {
      scanner.dispose();
      this.scanner = null;
      this.tool.replaceChildren(notice("Joining session…"));
      void this.access
        .joinSession(code)
        .then(() => {
          if (this.lifetime.active) this.tool.replaceChildren();
        })
        .catch((failure: unknown) => {
          if (this.lifetime.active) {
            error.textContent = message(failure);
            this.tool.replaceChildren(
              panel(
                error,
                button("Scan again", "button", () => this.scan()),
              ),
            );
          }
        });
    });
    this.scanner = scanner;
    this.tool.replaceChildren(
      panel(
        heading(2, "Scan session QR code"),
        scanner.element,
        button("Cancel", "button button--secondary", () => {
          scanner.dispose();
          this.scanner = null;
          this.tool.replaceChildren();
        }),
      ),
    );
  }
  private async mutate(action: () => Promise<void>): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      await action();
    } finally {
      this.busy = false;
    }
  }
  private render(): void {
    this.content.replaceChildren(
      ...this.access.sessions.map((session) => this.session(session)),
    );
    if (this.access.sessions.length === 0)
      this.content.append(
        notice(
          this.access.initialized
            ? "No active sessions. Start or join a session to begin canvassing."
            : "Loading sessions…",
        ),
      );
    if (this.access.error) this.error.textContent = message(this.access.error);
  }
  private session(session: Session): HTMLElement {
    const card = panel(
      element("p", "eyebrow", session.project.branch.name),
      heading(2, session.project.name),
      element("p", null, "Remaining: ", countdown(session.expires_at)),
      element(
        "p",
        "muted",
        `Started ${dateLabel(session.started_at)} · ${session.participants.length} participants`,
      ),
      button("Canvass in this session", "button", () => {
        this.access.select({ kind: "session", id: session.id });
        this.canvass();
      }),
    );
    if (!this.access.manager) return card;
    const issued = this.access.issued.get(session.id);
    if (issued) {
      const qr = element(
        "div",
        "qr-pass",
        qrImage(issued.join_code, `Join ${session.project.name} session`),
      );
      qr.dataset.expiry = session.expires_at;
      card.append(qr, notice("Members in your organisation can scan this code to join."));
    }
    const regenerate = runButton(
      issued ? "Regenerate QR code" : "Generate QR code",
      () =>
        this.mutate(async () => {
          if (
            !(await confirmAction(
              "Regenerate session QR code?",
              "Any previously shared QR code for this session will stop working.",
              "Regenerate",
            )) ||
            !this.lifetime.active
          )
            return;
          await this.access.regenerate(session.id);
        }),
      this.error,
      "button button--secondary",
    );
    const extend = runButton(
      "Extend by 1 hour",
      () => this.mutate(() => this.access.extend(session.id)),
      this.error,
      "button button--secondary",
    );
    extend.disabled = Date.parse(session.expires_at) >= Date.parse(session.maximum_ends_at);
    const end = runButton(
      "End session",
      () =>
        this.mutate(async () => {
          if (
            !(await confirmAction(
              "End this session?",
              "Participants will immediately lose the access granted by this session.",
              "End session",
            )) ||
            !this.lifetime.active
          )
            return;
          await this.access.end(session.id);
        }),
      this.error,
      "button button--danger",
    );
    card.append(actions(regenerate, extend, end), heading(3, "Participants"));
    if (session.participants.length === 0) card.append(notice("No members have joined yet."));
    for (const person of session.participants)
      card.append(
        element(
          "div",
          "participant",
          element("span", null, person.user.display_name),
          runButton(
            "Kick",
            () =>
              this.mutate(async () => {
                if (
                  !(await confirmAction(
                    `Remove ${person.user.display_name}?`,
                    "This revokes their session access and prevents them from rejoining.",
                    "Remove member",
                  )) ||
                  !this.lifetime.active
                )
                  return;
                await this.access.kick(session.id, person.user.id);
              }),
            this.error,
            "button button--danger",
          ),
        ),
      );
    return card;
  }
  public dispose(): void {
    this.lifetime.dispose();
    this.scanner?.dispose();
  }
}
